import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';
import type { FieldValue } from '../fieldValues.js';
import { LookupSelect } from '../LookupSelect.js';
import type { FieldOptionsResult } from '../useFieldOptions.js';

export const COMPARISON_ACTION = 'getComparison';
export const COMPARE_VALUES = 'compare_values';
export const COUNTRY_BREAKDOWN = 'country';

export type SidePair = [string, string];

interface CountryPairSetupProps {
  labels: Record<string, string>;
  sides: SidePair;
  resolved: FieldOptionsResult;
  onChange: (sides: SidePair) => void;
}

export function sidesOf(value: FieldValue | undefined): SidePair {
  const [a = '', b = ''] = Array.isArray(value) ? value.map(String) : [];
  return [a, b];
}

/** Names of the sides still unpicked, for the Run button's "Fill in …" hint. */
export function missingSides(sides: SidePair, names: SidePair): string[] {
  return sides.flatMap((value, index) => (value === '' ? [names[index] ?? ''] : []));
}

// Each side offers every country but the other side's, so the pair is always distinct.
function without(resolved: FieldOptionsResult, taken: string): FieldOptionsResult {
  if (taken === '') return resolved;
  const kept = (options: FieldOptionsResult['options']) =>
    options.filter((option) => option.id !== taken);
  return {
    ...resolved,
    options: kept(resolved.options),
    ...(resolved.more === undefined ? {} : { more: kept(resolved.more) }),
  };
}

function Side({
  id,
  label,
  resolved,
  value,
  onChange,
}: {
  id: 'a' | 'b';
  label: string | undefined;
  resolved: FieldOptionsResult;
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <Stack data-testid={`comparison-side-${id}`}>
      <LookupSelect
        label={label ?? ''}
        hideLabel={label === undefined}
        tooltip={null}
        resolved={resolved}
        value={value}
        onChange={onChange}
      />
    </Stack>
  );
}

export function CountryPairSetup({
  labels,
  sides,
  resolved,
  onChange,
}: CountryPairSetupProps): ReactNode {
  const [a, b] = sides;
  return (
    <Stack direction="row" sx={{ gap: 4, flexWrap: 'wrap', alignItems: 'start' }}>
      <Side
        id="a"
        label={labels['side_a']}
        resolved={without(resolved, b)}
        value={a}
        onChange={(next) => onChange([next, b])}
      />
      <Side
        id="b"
        label={labels['side_b']}
        resolved={without(resolved, a)}
        value={b}
        onChange={(next) => onChange([a, next])}
      />
    </Stack>
  );
}
